import h from 'hyperscript';
import merge from 'deepmerge';
import querystring from 'querystring';
import * as is from 'utilities/is';
import { safeJSON } from 'utilities/parsers';
import { logRemotely } from 'utilities/logging';
import { parseLocator } from 'utilities/network';
import { relativeToAbsolute } from 'utilities/parsers';
import { WRAPPER_TYPES, OVERRIDES_PROVIDER_KEY } from 'constants';

export class ConfigurationBuilder {
    constructor() {
        this.queryParams = this.getQueryParams();
    }

    generate(url) {
        const additionalSettings = {
            ...this.getScreenshotOptions(),
            ...this.getProjectSettings()
        };

        return new Promise((resolve, reject) => {
            if (this.hasRemoteConfig || url) {
                this.getRemoteOverrides(url)
                    .then((overrides) => ({
                        ...additionalSettings,
                        overrides: merge(overrides,this.getOverrides())
                    }))
                    .then(resolve)
                    .catch(err => {
                        console.error('error loading config', url);
                        logRemotely('error loading interactive video config');
                        throw err;
                    })
            } else {
                resolve({
                    ...additionalSettings,
                    overrides: this.getOverrides()
                });
            }
        });
    }

    getQueryParams() {
        const params = querystring.parse(window.location.search.replace('?', ''));

        if (params.variables) {
            params.variables = safeJSON(params.variables);
        }

        return params;
    }

    getScreenshotOptions() {
        if (this.queryParams.screenshot === 'true') {
            let complete = false;
            const startTime = this.queryParams.startTime || 0;

            return {
                editor: true, // prevent gates from being applied to screenshots (see BaseVideoAdapter.onPlayerEvent)
                onTime: time => {
                    if (!complete && time >= startTime) {
                        complete = true;
                        setTimeout(function () {
                            console.log('injecting cheese');
                            document.body.appendChild(<div id="url2png-cheese"></div>);
                        }, 500);
                    }
                }
            }
        }

        return {};
    }

    getProjectSettings() {
        const {
            projectId,
            apiKey,
            playerType,
            startTime,
            overrides,
            environment,
            controls,
            captions,
            scaleToContainer,
            thumbnail,
            loop
        } = this.queryParams;

        const settings = {
            version: HAPYAK_VERSION,
            projectId,
            apiKey,
            playerType: playerType || WRAPPER_TYPES.VIDEO_JS
        };

        if (is.truthy(startTime)) {
            settings.startTime = startTime;
        }

        if (overrides) {
            settings.overrides = overrides;
        }

        if (environment) {
            settings.environment = environment;
        }

        if (is.defined(controls)) {
            settings.controls = controls === "true";
        }

        if (is.defined(scaleToContainer)) {
            settings.scaleToContainer = scaleToContainer === "true";
        }

        if (is.defined(thumbnail)) {
            settings.thumbnail = thumbnail;
        }

        if (is.defined(loop)) {
            settings.loop = loop === "true";
        }

        if (is.defined(captions)) {
            settings.captions = captions;
        }

        return settings;
    }

    getOverrides() {
        return this.queryParams.overrides ? JSON.parse(this.queryParams.overrides) : {};
    }

    getRemoteOverrides(remoteUrl) {
        if (is.fn(window[OVERRIDES_PROVIDER_KEY])) {
            return window[OVERRIDES_PROVIDER_KEY]();
        }

        return remoteUrl ? getJSON(remoteUrl) : this.getRemoteConfig().then(getJSON);

        function getJSON(url) {
            const basePath = url.replace(/[^\/]+$/, '');

            return (
                fetch(url, { credentials: 'same-origin' })
                    .then(res => res.json())
                    .then(json => relativeToAbsolute(basePath, json))
            )
        }
    }

    get minimumConfig() {
        return this.queryParams && (
            (this.queryParams.projectId && this.queryParams.apiKey) ||
            (this.queryParams.overrides || this.queryParams.overridesUrl)
        );
    }

    get hasRemoteConfig() {
        return !!(this.queryParams && this.queryParams.overridesUrl);
    }

    getRemoteConfig() {
        return parseLocator(this.queryParams.overridesUrl);
    }
}